/**
 * RawScore (arcade) and RatedScore (handicap). Formulas: docs/GAME_DESIGN.md
 * § Scoring; rationale + anti-exploit: docs/SCORING.md. Constants: config.ts SCORING.
 *
 * Rounding: every raw part is a whole number (so GameState.raw is an integer).
 * RatedScore is a property of the whole run (a weighted average), computed by
 * ratedScoreForRun and rounded once.
 */
import { SCORING } from '../config.ts';
import type { ScoreContext, ScoreDelta } from '../types.ts';
import { weightedAnswerValue } from './rated.ts';
import { rawParts } from './raw.ts';
import type { ScoringConstants } from './types.ts';

export type { ScoringConstants } from './types.ts';
export type { ScorePartLabel } from './raw.ts';
export type { RatedAnswer } from './rated.ts';
export { levelUpBonus, streakMultiplier } from './raw.ts';
export {
  difficultyWeight,
  ratedScoreForRun,
  answerCredit,
  answerTime,
  ratingAnchor,
  relevanceWeight,
  weightedAnswerValue,
} from './rated.ts';

/**
 * Score one answer.
 * - `raw`: the arcade points this answer adds to the run's RawScore (whole points).
 * - `rated`: the answer's weighted contribution UNIT·m·w(D)·v in rated points —
 *   its term in the numerator of the run's RatedScore average (e.g. +1120 for a
 *   level-1 answer at 0.8 × par, −600 for a miss), unrounded. It is not added to
 *   the run score: the run score is Σ of these ÷ (Σ m + N0), see ratedScoreForRun.
 *   `ctx.referenceRating` is used as the relevance anchor; the engine passes
 *   max(referenceRating, peak rating so far).
 */
export function scoreAnswer(ctx: ScoreContext, k: ScoringConstants = SCORING): ScoreDelta {
  const parts = rawParts(ctx, k);
  return {
    raw: parts.reduce((sum, p) => sum + p.raw, 0),
    rated: weightedAnswerValue(ctx.record, ctx.referenceRating, k),
    parts,
  };
}
